import type { ManualChapter } from './types'

export const adminChapter: ManualChapter = {
  id: 'admin',
  number: 11,
  title: 'Guida Amministratore',
  description: 'Gestione piattaforma e configurazioni',
  icon: 'CogIcon',
  sections: [
    {
      id: 'overview',
      title: 'Pannello Amministrazione',
      requirement: '§3.1 Pannello di amministrazione completo',
      content: `
## Pannello di Amministrazione

L'area amministrativa permette di gestire tutti gli aspetti della piattaforma.

### Accesso

1. Effettua il login con un account **amministratore**
2. Clicca sull'icona **ingranaggio** nella sidebar
3. Oppure vai su **Impostazioni** → **Amministrazione**

### Sezioni Disponibili

| Sezione | Funzione |
|---------|----------|
| **Dashboard** | Panoramica e statistiche |
| **Utenti** | Gestione membri |
| **Corsi** | Gestione formazione |
| **Forum** | Moderazione discussioni |
| **Eventi** | Gestione calendario |
| **Documenti** | Repository documentale |
| **Task Force** | Gruppi di lavoro |
| **Gamification** | Badge e punti |
| **Impostazioni** | Configurazioni sistema |

### Ruoli Amministrativi

| Ruolo | Permessi |
|-------|----------|
| **Super Admin** | Accesso completo a tutto |
| **Admin** | Gestione utenti e contenuti |
| **Moderatore** | Solo moderazione forum |
| **Content Manager** | Solo gestione contenuti |
`
    },
    {
      id: 'users',
      title: 'Gestione Utenti',
      requirement: '§3.2 Gestione utenti con SSO',
      content: `
## Gestione Utenti

### Lista Utenti

Visualizza tutti gli utenti con:
- **Nome** e cognome
- **Email**
- **Ruolo**
- **Stato** (attivo/inattivo)
- **Data registrazione**
- **Ultimo accesso**

### Creare un Utente

1. Clicca su **"Nuovo Utente"**
2. Compila i campi:

| Campo | Obbligatorio |
|-------|--------------|
| Email | Si |
| Nome | Si |
| Cognome | Si |
| Ruolo | Si |
| Password | Si (o invito) |

3. Clicca **"Crea"**

### Modificare un Utente

1. Clicca sull'utente nella lista
2. Modifica i campi desiderati
3. Clicca **"Salva"**

### Azioni Disponibili

| Azione | Effetto |
|--------|---------|
| **Attiva/Disattiva** | Abilita o blocca l'accesso |
| **Reset Password** | Invia email per nuovo accesso |
| **Cambia Ruolo** | Modifica i permessi |
| **Elimina** | Rimuove l'utente (irreversibile) |

### Import Massivo

Per importare molti utenti:
1. Prepara un file **CSV** con i dati
2. Clicca su **"Importa"**
3. Carica il file
4. Mappa le colonne
5. Conferma l'import

### SSO (Single Sign-On)

Se configurato, gli utenti possono accedere con:
- Account aziendale (Active Directory)
- Google Workspace
- Altri provider configurati
`
    },
    {
      id: 'courses-admin',
      title: 'Gestione Corsi',
      requirement: '§3.3 LMS integrato',
      content: `
## Gestione Corsi

### Creare un Corso

1. Vai su **Corsi** → **Nuovo Corso**
2. Compila le informazioni base:

| Campo | Descrizione |
|-------|-------------|
| Titolo | Nome del corso |
| Descrizione | Di cosa tratta |
| Categoria | Area tematica |
| Livello | Base/Intermedio/Avanzato |
| Durata | Tempo stimato |
| Immagine | Copertina del corso |

3. Aggiungi i **moduli** e le **lezioni**
4. Configura i **quiz**
5. Pubblica il corso

### Struttura del Corso

\`\`\`
Corso
├── Modulo 1
│   ├── Lezione 1 (video)
│   ├── Lezione 2 (PDF)
│   └── Quiz modulo
├── Modulo 2
│   ├── Lezione 3 (HTML)
│   └── Lezione 4 (SCORM)
└── Quiz finale
\`\`\`

### Tipi di Contenuto

| Tipo | Formati |
|------|---------|
| **Video** | YouTube, Vimeo, upload |
| **Documenti** | PDF, Word |
| **HTML** | Contenuto testuale |
| **SCORM** | Pacchetti interattivi |
| **Quiz** | Domande e risposte |

### Assegnare Corsi

- **Obbligatori**: Assegna a utenti/gruppi
- **Opzionali**: Disponibili nel catalogo
- **Con prerequisiti**: Richiedono altri corsi

### Reportistica

Visualizza:
- **Iscrizioni** per corso
- **Completamenti** e percentuali
- **Risultati quiz** medi
- **Tempo** impiegato
`
    },
    {
      id: 'forum-admin',
      title: 'Moderazione Forum',
      requirement: '§3.4 Strumenti di moderazione',
      content: `
## Moderazione Forum

### Gestione Categorie

1. Vai su **Forum** → **Categorie**
2. Crea, modifica o elimina categorie
3. Imposta i **permessi** per categoria

### Coda di Moderazione

Visualizza contenuti segnalati:
- **Post** segnalati dagli utenti
- **Thread** problematici
- **Utenti** con comportamenti sospetti

### Azioni di Moderazione

| Azione | Effetto |
|--------|---------|
| **Approva** | Conferma il contenuto |
| **Rimuovi** | Elimina il contenuto |
| **Modifica** | Correggi il contenuto |
| **Sposta** | Cambia categoria |
| **Chiudi** | Blocca le risposte |
| **Pin** | Fissa in alto |
| **Avviso** | Notifica all'autore |

### Gestione Segnalazioni

1. Apri la **segnalazione**
2. Valuta il contenuto
3. Decidi l'azione:
   - **Ignora**: Falsa segnalazione
   - **Rimuovi**: Contenuto inappropriato
   - **Ban**: Utente ripetutamente problematico

### Contenuti Ufficiali

Per marcare contenuti come **ufficiali**:
1. Apri il thread o post
2. Clicca su **"Segna come ufficiale"**
3. Il contenuto avra un badge distintivo
`
    },
    {
      id: 'events-admin',
      title: 'Gestione Eventi',
      requirement: '§3.5 Gestione eventi con Jitsi Meet',
      content: `
## Gestione Eventi

### Creare un Evento

1. Vai su **Eventi** → **Nuovo Evento**
2. Compila i dettagli:

| Campo | Descrizione |
|-------|-------------|
| Titolo | Nome dell'evento |
| Tipo | Webinar, Workshop, etc. |
| Data/Ora | Quando si terra |
| Durata | Quanto durera |
| Modalita | Online/In presenza/Ibrido |
| Posti | Numero massimo partecipanti |
| Descrizione | Di cosa tratta |

3. Aggiungi l'**agenda** (opzionale)
4. Configura le **notifiche**
5. Pubblica l'evento

### Eventi Online (Jitsi Meet)

Per eventi online:
1. Seleziona modalita **Online**
2. Il sistema genera automaticamente la stanza Jitsi
3. Il link sara visibile ai partecipanti

### Gestione Iscrizioni

Visualizza:
- **Iscritti** all'evento
- **Lista d'attesa** (se posti esauriti)
- **Presenze** (dopo l'evento)

### Materiali Post-Evento

1. Apri l'evento passato
2. Vai alla sezione **Materiali**
3. Carica slide, video, documenti
4. I partecipanti riceveranno notifica

### Community Day

Per il Community Day:
1. Attiva la sezione **Proposte talk**
2. Apri le **votazioni**
3. Raccogli le proposte
4. Pubblica il **programma finale**
`
    },
    {
      id: 'settings',
      title: 'Impostazioni Sistema',
      requirement: '§3.6 Configurazione piattaforma',
      content: `
## Impostazioni Sistema

### Impostazioni Generali

| Impostazione | Descrizione |
|--------------|-------------|
| Nome piattaforma | Titolo visualizzato |
| Logo | Immagine aziendale |
| Lingua | Italiano/Inglese |
| Fuso orario | Per date ed eventi |
| Email sistema | Mittente notifiche |

### Tema e Aspetto

Personalizza:
- **Colore primario** (bottoni, link)
- **Colore sidebar** e header
- **Sfondo** pagine
- **Logo** e branding
- **Testi** login e copyright

### Email e Notifiche

Configura:
- **Template** email
- **Frequenza** digest
- **Tipi** di notifica attivi
- **Server SMTP** per invio

### Sicurezza

| Impostazione | Funzione |
|--------------|----------|
| Complessita password | Requisiti minimi |
| Scadenza password | Cambio periodico |
| 2FA | Autenticazione a due fattori |
| Sessioni | Timeout inattivita |
| IP whitelist | Accesso limitato |

### Integrazioni

Configura connessioni con:
- **SSO** (SAML, OAuth)
- **Storage** (S3, Azure)
- **Analytics** (Google Analytics)
- **Jitsi Meet** (server custom)
`
    },
    {
      id: 'analytics',
      title: 'Analytics e Report',
      requirement: '§3.7 Dashboard analytics',
      content: `
## Analytics e Report

### Dashboard Analytics

La dashboard mostra KPI principali:

| Metrica | Descrizione |
|---------|-------------|
| **Utenti attivi** | Giornalieri/Settimanali/Mensili |
| **Corsi completati** | Totale e trend |
| **Post forum** | Attivita community |
| **Eventi** | Partecipazione |

### Report Disponibili

| Report | Contenuto |
|--------|-----------|
| **Utenti** | Registrazioni, accessi, attivita |
| **Formazione** | Iscrizioni, completamenti, quiz |
| **Forum** | Post, risposte, engagement |
| **Eventi** | Iscrizioni, presenze |
| **Gamification** | Punti, badge, classifiche |

### Esportazione

Ogni report puo essere esportato in:
- **PDF**: Per presentazioni
- **Excel**: Per elaborazioni
- **CSV**: Per import in altri sistemi

### Filtri Temporali

Analizza dati per periodo:
- Ultimi 7 giorni
- Ultimo mese
- Ultimo trimestre
- Anno corrente
- Intervallo personalizzato

### Grafici

Visualizzazioni disponibili:
- **Linea**: Trend temporali
- **Barra**: Confronti
- **Torta**: Distribuzioni
- **Tabella**: Dati dettagliati
`
    },
    {
      id: 'backup',
      title: 'Backup e Manutenzione',
      content: `
## Backup e Manutenzione

### Backup Automatici

Il sistema esegue backup automatici di:
- **Database**: Tutti i dati
- **File**: Allegati e documenti
- **Configurazioni**: Impostazioni

### Frequenza

| Tipo | Frequenza | Retention |
|------|-----------|-----------|
| Completo | Settimanale | 4 settimane |
| Incrementale | Giornaliero | 7 giorni |
| Configurazioni | Ad ogni modifica | 30 versioni |

### Ripristino

In caso di necessita:
1. Contatta il supporto tecnico
2. Indica la data del backup desiderato
3. Il team procedera al ripristino

### Manutenzione Programmata

Le manutenzioni sono comunicate via:
- **Email** agli amministratori
- **Banner** sulla piattaforma
- Con **anticipo** di almeno 24 ore

### Log di Sistema

Disponibili log per:
- **Accessi** utenti
- **Modifiche** configurazioni
- **Errori** sistema
- **Azioni** amministrative

> **Nota**: I log sono accessibili solo ai Super Admin.
`
    }
  ]
}
